import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getIcon } from '../../lib/icons';
import { motion } from 'framer-motion';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: string;
  href?: string;
  features?: string[];
  compact?: boolean;
}

export default function ServiceCard({
  title,
  description,
  icon,
  href,
  features,
  compact = false,
}: ServiceCardProps) {
  const IconComponent = getIcon(icon);

  const cardContent = (
    <>
      <div className="p-3 bg-primary-50 rounded-xl w-fit mb-4 group-hover:bg-primary-100 transition-colors">
        <IconComponent className="w-6 h-6 text-primary-600" />
      </div>
      <h3 className="heading-3 text-charcoal-900 mb-2">{title}</h3>
      <p className="text-charcoal-600 text-sm leading-relaxed mb-4">
        {description}
      </p>
      {features && !compact && (
        <ul className="space-y-2 mb-4">
          {features.slice(0, 3).map((feature, index) => (
            <li
              key={index}
              className="flex items-center gap-2 text-sm text-charcoal-600"
            >
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
              {feature}
            </li>
          ))}
        </ul>
      )}
      {href && (
        <div className="flex items-center gap-2 text-primary-600 font-medium text-sm group-hover:gap-3 transition-all">
          Learn More
          <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </>
  );

  if (href) {
    return (
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
      >
        <Link
          to={href}
          className="group block p-6 bg-white rounded-2xl border border-charcoal-100 hover:border-primary-200 hover:shadow-lg transition-all h-full"
        >
          {cardContent}
        </Link>
      </motion.div>
    );
  }

  return (
    <div className="group p-6 bg-white rounded-2xl border border-charcoal-100 h-full">
      {cardContent}
    </div>
  );
}
